import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { IButtonProps, Image, Stack } from '@fluentui/react';
import { Heading1, isSmallScreen, Text } from 'common';
import { svgImageStyles } from 'common/utilities/SvgImageStyles';
import { useLocalizedStrings } from 'i18n';
import { useAppSelector, useAssetPath } from 'store';

import { ActionButton, PrimaryButton } from '../Buttons/v8';

import { containerTokens, useStyles } from './ErrorBasePage.styles';

interface ErrorBasePageProps {
  imageFileName: string | { Light: string; Dark: string };
  headerText: string;
  detailsMessage: string;
  primaryButton?: IButtonProps;
  secondaryButton?: IButtonProps;
}

const ErrorBasePage: React.FunctionComponent<ErrorBasePageProps> = (props: ErrorBasePageProps) => {
  const { imageFileName, headerText, detailsMessage, primaryButton, secondaryButton } = props;
  const imageAsset = useAssetPath(imageFileName);
  const styles = useStyles();
  const { screenSize } = useAppSelector((state) => state.layout);
  const smallScreen = isSmallScreen(screenSize);
  const { t } = useLocalizedStrings();

  const buttons = useMemo(() => {
    if (!primaryButton && !secondaryButton) return null;

    return (
      <Stack horizontal={!smallScreen} className={styles.buttonsContainer} tokens={{ childrenGap: 12 }}>
        {primaryButton && <PrimaryButton {...primaryButton} />}
        {secondaryButton && <ActionButton {...secondaryButton} />}
      </Stack>
    );
  }, [smallScreen, primaryButton, secondaryButton, styles.buttonsContainer]);

  const warningImage = useMemo(() => {
    const imageDimensions = smallScreen ? '204px' : '312px';
    return <Image src={imageAsset} styles={{ ...svgImageStyles }} height={imageDimensions} width={imageDimensions} alt="" />;
  }, [imageAsset, smallScreen]);

  return (
    <>
      <Helmet>
        <title>{t('Errors.PageTitle')}</title>
        <meta name="Error" />
      </Helmet>
      <Stack
        horizontal={!smallScreen}
        className={styles.content}
        horizontalAlign="space-between"
        verticalAlign="center"
        tokens={containerTokens}
      >
        <Stack className={styles.verticalStack}>
          <Heading1 variant="Title1">{headerText}</Heading1>
          <Text variant="Body1" className={styles.detailsText}>
            {detailsMessage}
          </Text>
          {!smallScreen && buttons}
        </Stack>
        {warningImage}
        {smallScreen && buttons}
      </Stack>
    </>
  );
};

export default ErrorBasePage;
